import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useQuery as useApolloQuery, useMutation as useApolloMutation } from "@apollo/client/react";
import { toast } from "sonner";
import { getBrowserSupabaseClient } from "@wtc/supabase";
import {
  GetDealsDocument,
  GetDealDocument,
  CreateDealDocument,
  UpdateDealDocument,
  DeleteDealDocument,
  type GetDealsQuery,
  type GetDealQuery,
  type CreateDealMutation,
  type UpdateDealMutation,
  type DeleteDealMutation,
} from "@/src/gql/graphql";
import type { Deal, CreateDealInput, UpdateDealInput } from "@/lib/types/database/deals";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";

import { stringifyDecimalFields, numberifyDecimalFields, normalizeDeals } from "./decimal-fields";
import {
  toDealFilterRpcRows,
  toDealSearchRpcRows,
  dealFilterRowToDeal,
  dealSearchRowToDeal,
  totalCountFromRpcRows,
} from "./rpc-rows";
import { normalizeGraphqlDatetimeInput } from "@/lib/graphql/serialize-input";
// Hoisted from an inline require() in the `not_followed_up_this_week`
// client-side filter branch. Same module specifiers, so resolution + behavior
// are unchanged; the modules are pure tag helpers with no import side effects.
import { getCurrentWeekFollowUpTag } from "@/lib/utils/follow-up-tagging";
import { isSnoozedThisWeek } from "@/lib/utils/task/snooze-tagging";
import { hasFutureScheduledFollowup } from "@/lib/utils/task/scheduled-followup-tagging";
import type { PaginatedResult } from "@/lib/types/table/entity-hooks-types";

/** Special runtime filters threaded through `usePaginated` alongside GraphQL variables. */
interface DealsRpcFilter {
  type: string;
  params?: Record<string, unknown>;
}

interface DealsClientSideFilter {
  type: string;
}

interface DealsPaginatedParams {
  searchTerm?: string;
  _rpcFilter?: DealsRpcFilter;
  _clientSideFilter?: DealsClientSideFilter;
  [key: string]: unknown;
}

/**
 * Shared mutation-input prep for the deal create/update paths. Drops Apollo
 * cache metadata (`__typename`, …), then serializes BigFloat + Datetime scalars
 * for the pg_graphql mutation. Generic over the codegen input type so each
 * caller keeps its exact typing (identical to the previous inline
 * `Object.fromEntries(...) as <Input>` + `normalizeGraphqlDatetimeInput(
 * stringifyDecimalFields(...))` sequence).
 */
export function prepareDealMutationInput<T extends object>(input: object): T {
  const cleanInput = Object.fromEntries(
    Object.entries(input).filter(([key]) => !key.startsWith("__"))
  ) as T;

  return normalizeGraphqlDatetimeInput(stringifyDecimalFields(cleanInput));
}

/**
 * Assembles the shared `usePaginated` return envelope (the fields common to the
 * RPC-filter, full-text-search, and GraphQL result branches). The GraphQL
 * branch appends `networkStatus` on top of this via spread. `error` is
 * OR-ed to `null` to match the previous `error || null` inline expressions.
 */
function toPaginatedResult(
  data: PaginatedResult<Deal> | undefined,
  source: {
    isLoading: boolean;
    error: Error | null;
    refetch: () => void;
  }
): {
  data?: PaginatedResult<Deal>;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  return {
    data,
    isLoading: source.isLoading,
    error: source.error || null,
    refetch: source.refetch,
  };
}

export const useDealHooks = (): EntityHooks<Deal> => ({
  queryDocument: GetDealsDocument,

  usePaginated: (params?: DealsPaginatedParams) => {
    // Check if this is a full-text search query
    const isFullTextSearch = params?.searchTerm;

    // Extract RPC filter if present
    const rpcFilter = params?._rpcFilter;

    // Extract client-side filter if present
    const clientSideFilter = params?._clientSideFilter;

    // Remove special filters from params before sending to GraphQL
    const graphqlParams = params ? { ...params } : {};
    delete graphqlParams._clientSideFilter;
    delete graphqlParams._rpcFilter;

    // Call both hooks unconditionally to maintain stable hook order
    // This is required by React's Rules of Hooks

    // React Query hook for RPC-based filtering
    const rpcQuery = useQuery({
      queryKey: ["deals-rpc-filter", rpcFilter?.type, rpcFilter?.params],
      queryFn: async (): Promise<{ deals: Deal[]; totalCount: number }> => {
        if (!rpcFilter) {
          throw new Error("RPC filter is required");
        }
        const supabase = getBrowserSupabaseClient();
        const { data, error } = await supabase.rpc(rpcFilter.type, rpcFilter.params || {});

        if (error) {
          throw new Error(`RPC filter failed: ${error.message}`);
        }

        // The browser client is untyped; validate + model the real (partial)
        // RPC row shape at the boundary instead of casting to Deal[].
        const rows = toDealFilterRpcRows(data);
        return {
          deals: normalizeDeals(rows.map(dealFilterRowToDeal)),
          totalCount: totalCountFromRpcRows(rows),
        };
      },
      enabled: !!rpcFilter,
      staleTime: 30000, // 30 seconds
    });

    // React Query hook for full-text search
    const searchQuery = useQuery({
      queryKey: ["deals-search", params?.searchTerm],
      queryFn: async (): Promise<{ deals: Deal[]; totalCount: number }> => {
        const supabase = getBrowserSupabaseClient();
        const { data, error } = await supabase.rpc("search_deals", {
          search_term: params?.searchTerm || "",
        });

        if (error) {
          throw new Error(`Search failed: ${error.message}`);
        }

        // search_deals returns a partial projection + window total_count; model
        // it explicitly rather than casting the untyped payload to Deal[].
        const rows = toDealSearchRpcRows(data);
        return {
          deals: normalizeDeals(rows.map(dealSearchRowToDeal)),
          totalCount: totalCountFromRpcRows(rows),
        };
      },
      enabled: !!params?.searchTerm && !rpcFilter,
      staleTime: 30000, // 30 seconds
    });

    // Apollo GraphQL hook for regular queries
    const graphqlQuery = useApolloQuery<GetDealsQuery>(GetDealsDocument, {
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-and-network",
      variables: graphqlParams || {},
      skip: !!isFullTextSearch || !!rpcFilter, // Skip when doing full-text search or RPC filter
    });

    // After all hooks are called, select which result to use
    // Priority: RPC filter > full-text search > GraphQL query
    if (rpcFilter) {
      return toPaginatedResult(
        rpcQuery.data
          ? {
              data: rpcQuery.data.deals,
              totalCount: rpcQuery.data.totalCount,
              pageInfo: undefined,
            }
          : undefined,
        {
          isLoading: rpcQuery.isLoading,
          error: rpcQuery.error || null,
          refetch: () => rpcQuery.refetch(),
        }
      );
    }

    if (isFullTextSearch) {
      return toPaginatedResult(
        searchQuery.data
          ? {
              data: searchQuery.data.deals,
              totalCount: searchQuery.data.totalCount,
              pageInfo: undefined,
            }
          : undefined,
        {
          isLoading: searchQuery.isLoading,
          error: searchQuery.error || null,
          refetch: () => searchQuery.refetch(),
        }
      );
    }

    // Handle regular paginated results
    let nodes: Deal[] = [];
    let totalCount = 0;
    let pageInfo = undefined;

    if (graphqlQuery.data && "dealsCollection" in graphqlQuery.data) {
      const paginatedData = graphqlQuery.data;
      nodes = normalizeDeals((paginatedData.dealsCollection?.edges
        ?.map((edge) => edge?.node)
        .filter((node): node is NonNullable<typeof node> => node != null) || []) as Deal[]);
      totalCount =
        (paginatedData.dealsCollection as { totalCount?: number } | undefined)?.totalCount ?? 0;
      pageInfo = paginatedData.dealsCollection?.pageInfo;
    }

    // Apply client-side filters (fallback for any remaining client-side filters)
    if (clientSideFilter && clientSideFilter.type === "not_followed_up_this_week") {
      const currentWeekTag = getCurrentWeekFollowUpTag();

      // Mirror the Slack reminder query (deal-followup/query.ts): a deal needs
      // follow-up only if it is NOT tagged followed-up this week, NOT snoozed
      // for this week, and NOT scheduled for a future follow-up week. Keep all
      // three exclusions in lockstep across the two surfaces + the RPC.
      nodes = nodes.filter((deal) => {
        const tags = deal.tags ?? [];
        if (tags.includes(currentWeekTag)) return false;
        if (isSnoozedThisWeek(tags)) return false;
        if (hasFutureScheduledFollowup(tags)) return false;
        return true;
      });

      // Update totalCount to reflect filtered results
      totalCount = nodes.length;
    }

    return {
      ...toPaginatedResult(
        graphqlQuery.data
          ? {
              data: nodes,
              totalCount,
              pageInfo,
            }
          : undefined,
        {
          isLoading: graphqlQuery.loading,
          error: graphqlQuery.error || null,
          refetch: () => graphqlQuery.refetch(),
        }
      ),
      networkStatus: graphqlQuery.networkStatus,
    };
  },

  useInfinite: () => ({
    data: undefined,
    isLoading: false,
    error: null,
    refetch: () => {},
    hasNextPage: false,
    isFetchingNextPage: false,
    fetchNextPage: () => {},
  }),

  useSingle: (id: string) => {
    const result = useApolloQuery<GetDealQuery>(GetDealDocument, {
      variables: { id },
      errorPolicy: "all",
      fetchPolicy: "cache-first",
    });

    const rawDeal = result.data?.dealsCollection?.edges?.[0]?.node;
    const deal = rawDeal ? numberifyDecimalFields(rawDeal as Deal) : undefined;

    return {
      data: deal,
      isLoading: result.loading,
      error: result.error || null,
      refetch: () => result.refetch(),
    };
  },

  useCreate: () => {
    const queryClient = useQueryClient();
    const [createDeal] = useApolloMutation<CreateDealMutation>(CreateDealDocument);

    return {
      mutateAsync: async (input: Partial<Deal> & { id?: string }) => {
        const finalInput = prepareDealMutationInput<CreateDealInput>(input);

        const { data } = await createDeal({
          variables: { input: finalInput },
        });

        if (!data?.insertIntodealsCollection?.records?.[0]) {
          throw new Error("Failed to create deal");
        }

        void queryClient.invalidateQueries({ queryKey: ["deals"] });
        toast.success("Deal created successfully");
        return numberifyDecimalFields(data.insertIntodealsCollection.records[0] as Deal);
      },
      isPending: false,
    };
  },

  useUpdate: () => {
    const queryClient = useQueryClient();
    const [updateDeal] = useApolloMutation<UpdateDealMutation>(UpdateDealDocument);

    return {
      mutateAsync: async (input: Partial<Deal> & { id: string }) => {
        const { id, ...updateInput } = input;
        const finalInput = prepareDealMutationInput<UpdateDealInput>(updateInput);

        const { data } = await updateDeal({
          variables: { id, input: finalInput },
        });

        if (!data?.updatedealsCollection?.records?.[0]) {
          throw new Error("Failed to update deal");
        }

        void queryClient.invalidateQueries({ queryKey: ["deals"] });
        toast.success("Deal updated successfully");
        return numberifyDecimalFields(data.updatedealsCollection.records[0] as Deal);
      },
      isPending: false,
    };
  },

  useDelete: () => {
    const queryClient = useQueryClient();
    const [deleteDeal] = useApolloMutation<DeleteDealMutation>(DeleteDealDocument);

    return {
      mutateAsync: async (id: string) => {
        const { data } = await deleteDeal({
          variables: { id },
        });

        if (!data?.deleteFromdealsCollection?.records?.[0]) {
          throw new Error("Failed to delete deal");
        }

        void queryClient.invalidateQueries({ queryKey: ["deals"] });
        toast.success("Deal deleted successfully");
        return id;
      },
      isPending: false,
    };
  },

  getFieldConfig: (fieldName: string) => {
    return {
      label: fieldName,
      type: "text",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: true,
      formSpan: 1,
      width: 150,
      minWidth: 100,
      maxWidth: 300,
    };
  },
  getTableConfig: () => ({
    entity: "deals",
    displayName: "Deals",
    fields: {},
  }),
});
